import * as yup from "yup";
import { FormatHelper, StringHelper } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import Drawer from "../../../assets/vfx/drawer/drawer";
import Form from "../../../assets/vfx/form/form";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../../assets/utils/lib";
import {
    QuestionnaireRequestPushNotificationDto,
    RequestPushNotificationDto,
} from "../../../services/dto";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";
import { FormikProps } from "formik";
import React, { useState } from "react";
import { Dialog } from "../../../assets/vfx/dialog/vfxDialog";
import { VfxDialogProps } from "../../../assets/vfx/dialog/vfxDialogProps";
import { QuestionnaireNotificationType } from "../../questionnaire/questionnaireForm/questionnaireContainer";
import DisplayData from "../../common/DisplayData/DisplayData";

export interface PushNotificationLookupProps {
    communities: DropdownOption[];
    userCommunities: DropdownOption[];
    notificationType: DropdownOption[];
    pageLink: DropdownOption[];
    zipCodes?: DropdownOption[];
}

interface PushNotificationFormProps {
    data?: RequestPushNotificationDto;
    loading?: boolean;
    show: boolean;
    submitToQuestionnaireApi?: boolean;
    questionnaireId?: string;
    onHide: () => void;
    onSave: (values: RequestPushNotificationDto) => DrawerFormSaveResult;
    lookups?: PushNotificationLookupProps;
    onSaveQuestionnaire?: (
        dto: QuestionnaireRequestPushNotificationDto
    ) => void | Promise<void>;
    communityId?: string;
    notificationTypeId?: string;
    disableCommunity: boolean;
    disableCommunitytoSend?: boolean;
    forceLinkToPage?: boolean;
    lockPageLink?: boolean;
    defaultPageLinkName?: string;
}

const NOTIFICATION_TYPE_URGENT = "2071a206-9975-413e-99ae-c05e2bfa7d34";
const linktoUrlLabel: string = "LinktoUrl";
const linktoPageLabel: string = "LinktoPage";

const validationSchema = yup.object().shape({
    fromCommunityId: yup
        .string()
        .required("Community is required.")
        .test(
            "not-empty",
            "Community is required",
            (value) => value !== Constants.emptyGuid
        ),
    sendToCommunityIds: yup
        .array(yup.string())
        .required("Communities to send to is required.")
        .min(1, "Communities to send to is required."),
    geometry: yup.array(yup.string()).optional().nullable(),
    notificationTypeId: yup.string().required("Notification type is required."),
    title: yup.string().required("Title is required."),
    message: yup.string().required("Message is required."),
    linkUrl: yup.string().nullable().url("Invalid URL.").optional(),
    scheduledTimeUtc: yup
        .date()
        .typeError("Please enter a valid date.")
        .nullable()
        .required("Scheduled date is required."),
    externalUrl: yup
        .string()
        .optional()
        .test("linkUrl-required", "Url is required", function (value) {
            const { actionLabel } = this.parent;
            if (actionLabel === linktoUrlLabel) {
                return !!value;
            }
            return true;
        }),
    pageLinkId: yup
        .string()
        .optional()
        .test("pagelink-required", "PageLink is required", function (value) {
            const { actionLabel } = this.parent;
            if (actionLabel === linktoPageLabel) {
                return !!value;
            }
            return true;
        })
        .nullable(),
});

export const PushNotificationForm = (props: PushNotificationFormProps) => {
    let pushNotification: RequestPushNotificationDto | undefined = props.data;
    const [isQuickReply, setIsQuickReply] = useState<boolean | undefined>(
        props.data?.quickReply ?? undefined
    );
    const [dialogProps, setDialogProps] = useState<VfxDialogProps | undefined>(
        undefined
    );

    const idValue: string = StringHelper.whenNullOrEmpty(
        props.data?.id,
        Constants.emptyGuid
    );
    const isEmptyId: boolean = idValue === Constants.emptyGuid;
    const header: string = isEmptyId
        ? "New Push Notification"
        : "Edit Push Notification";
    const questionnaireNotificationTypes: string[] = [
        QuestionnaireNotificationType.poll,
        QuestionnaireNotificationType.feedback,
        QuestionnaireNotificationType.survey,
    ];

    const handleSaveQuestionnaireRequestPushNotification = async (dto: QuestionnaireRequestPushNotificationDto) => {
        await props.onSaveQuestionnaire?.(dto);
        props.onHide();
    };

    const onSubmit = async (data: RequestPushNotificationDto): Promise<void> => {
        const updatedData: RequestPushNotificationDto = {
            ...data,
            status: "new-request",
            priority: data.priority ?? 100,
            pageLinkId:
                data.pageLinkId && data.pageLinkId !== "" ? data.pageLinkId : undefined,
        };
        if (props.submitToQuestionnaireApi) {
            const dto: QuestionnaireRequestPushNotificationDto = {
                id: updatedData.id,
                scheduledTimeUtc: updatedData.scheduledTimeUtc ?? null,
                notificationTypeId: updatedData.notificationTypeId!,
                status: updatedData.status,
                priority: updatedData.priority,
                title: updatedData.title!,
                message: updatedData.message!,
                sentDate: updatedData.sentDate,
                fromCommunityId: updatedData.fromCommunityId!,
                sendToCommunityIds: Array.isArray(
                    (updatedData as any).sendToCommunityIds
                )
                    ? (updatedData as any).sendToCommunityIds
                    : [],
                subtitle: (updatedData as any).subtitle,
                actionLabel: updatedData.actionLabel,
                externalUrl: updatedData.externalUrl,
                pageLinkId: updatedData.pageLinkId,
                questionnaireId: props.questionnaireId!,
                quickReply: updatedData.quickReply,
                geometry: []
            };

            setDialogProps({
                contentTitle: "Send Poll Notification",
                contentText: "Are you sure you want to send this poll notification?",
                confirmText: "Yes, Send",
                closeText: "Cancel",
                open: true,
                onClose: () => setDialogProps(undefined),
                onConfirm: () => {
                    setDialogProps(undefined);
                    void handleSaveQuestionnaireRequestPushNotification(dto);
                },
            });
            return;
        }
        return Promise.resolve(props.onSave(updatedData)).then((result) => {
            if (hasClientErrorOperationStatusCode(result)) {
                return;
            }

            props.onHide();
        });
    };
    React.useEffect(() => {
        if (props.forceLinkToPage) {
            setReferenceType("page");
            return;
        }
        const isUrl = StringHelper.isNotNullOrEmpty(props.data?.externalUrl);
        const isPage: boolean = StringHelper.isNotNullOrEmpty(
            props.data?.pageLinkId
        );
        setReferenceType(isPage ? "page" : isUrl ? "url" : "url");
    }, [props.data?.id, props.data?.externalUrl, props.data?.pageLinkId]);

    type referenceTypeOptions = "url" | "page";
    const [referenceType, setReferenceType] =
        React.useState<referenceTypeOptions>(
            props.forceLinkToPage ? "page" : "url"
        );
    const referenceTypeOptions: DropdownOption[] = [
        {
            key: "page",
            value: "page",
            display: "Page",
        },
        {
            key: "url",
            value: "url",
            display: "External Site",
        },
    ];

    const communityAutoOptions = React.useMemo(
        () =>
            (props.lookups?.userCommunities ?? []).map((o) => ({
                id: String(o.value),
                name: String(o.display),
            })),
        [props.lookups?.userCommunities]
    );

    const pollPageLink: string = "e172c120-3f78-f011-8ef6-00155d112802";

    const forcedReferenceType = props.forceLinkToPage ? "page" : undefined;
    const forcedPageLinkId = props.forceLinkToPage
        ? pollPageLink ?? undefined
        : undefined;

    const getQuestionnaireNotificationTypeLabel = (id?: string): string => {
        if (id === QuestionnaireNotificationType.poll) return "Poll";
        if (id === QuestionnaireNotificationType.feedback) return "Feedback";
        return Constants.emptyString;
    };

    return (
        <>
            <Drawer.Form
                className="w-30"
                show={props.show}
                backdrop={true}
                placement="end"
                scroll={true}
                onHide={props.onHide}
                title={header}
                initialValues={
                    pushNotification ?? {
                        fromCommunityId: props.communityId,
                        notificationTypeId: props.notificationTypeId,
                        actionLabel: "",
                        externalUrl: "",
                        pageLinkId: forcedPageLinkId,
                        referenceType: forcedReferenceType ?? "url",
                        sendToCommunityIds:
                            communityAutoOptions?.map((community) => community.id) ?? [],
                        geometry: [],
                        scheduledTimeUtc: FormatHelper.formatDateTimeForDatePicker(
                            new Date()
                        ),
                    }
                }
                validationSchema={validationSchema}
                on_Submit={(vals) => {
                    void onSubmit(vals);
                }}
                enableReinitialize
            >
                {(formikProps: FormikProps<RequestPushNotificationDto>) => {
                    React.useEffect(() => {
                        if (!props.show) return;

                        if (props.forceLinkToPage) {
                            formikProps.setFieldValue("referenceType", "page");
                            formikProps.setFieldValue("externalUrl", "");
                            if (!formikProps.values.pageLinkId && pollPageLink) {
                                formikProps.setFieldValue("pageLinkId", pollPageLink);
                            }
                        }
                    }, [props.show, props.forceLinkToPage, pollPageLink]);

                    const notificationTypeIdValue: string | undefined =
                        formikProps.values.notificationTypeId ?? props.notificationTypeId;

                    const questionnaireLabel: string =
                        getQuestionnaireNotificationTypeLabel(notificationTypeIdValue);

                    const communityValue: string | undefined =
                        formikProps.values.fromCommunityId ?? props.communityId;

                    const communityLabel = props.lookups?.communities?.find(
                        (item) => item.value === communityValue
                    )?.display;

                    return (
                        <>
                            <Form.PanelSection iconClassName="bi bi-people" title="1. Recipients">
                                {props.disableCommunitytoSend ? (
                                    props?.data?.sendToCommunityIds && (
                                        <DisplayData
                                            text="Community to Send To"
                                            importedData={props?.data?.sendToCommunityIds
                                                ?.map(
                                                    (communityToSendId) =>
                                                        communityAutoOptions.find(
                                                            (x) => x.id === communityToSendId
                                                        )?.name
                                                )
                                                .filter(Boolean)
                                                .map((name, index) => (
                                                    <div key={index}>{name}</div>
                                                ))}
                                        />
                                    )
                                ) : (
                                    <Form.Autocomplete
                                        name="sendToCommunityIds"
                                        label="Community to Send To"
                                        isLoading={props.loading}
                                        multiple
                                        placeholder="Select Communities to send to"
                                        options={communityAutoOptions}
                                        disabled={props.disableCommunitytoSend}
                                    />
                                )}

                                {props.disableCommunitytoSend ? (
                                    props?.data?.geometry &&
                                    props.data.geometry.length > 0 && (
                                        <DisplayData
                                            text="Geometry"
                                            importedData={props?.data?.geometry
                                                ?.map(
                                                    (zipId) =>
                                                        props.lookups?.zipCodes?.find((x) => x.value === zipId)
                                                            ?.display
                                                )
                                                .filter(Boolean)
                                                .map((name, index) => (
                                                    <div key={index}>{name}</div>
                                                ))}
                                        />
                                    )
                                ) : (
                                    <Form.Autocomplete
                                        name="geometry"
                                        label="Geometry"
                                        isLoading={props.loading}
                                        multiple
                                        placeholder="Select Zip Codes"
                                        options={
                                            props.lookups?.zipCodes?.map((o) => ({
                                                id: String(o.value),
                                                name: String(o.display),
                                            })) ?? []
                                        }
                                    />
                                )}
                            </Form.PanelSection>

                            <Form.PanelSection
                                iconClassName="bi bi-bell"
                                title="2. Notification"
                                style={{ marginTop: 16 }}
                            >
                                {props?.disableCommunitytoSend ? (
                                    <DisplayData
                                        text="Notification Type"
                                        importedData={questionnaireLabel}
                                    />
                                ) : (
                                    <Form.Dropdown
                                        name="notificationTypeId"
                                        label="Notification Type"
                                        isLoading={props.loading}
                                        blankOption={{
                                            key: Constants.emptyGuid,
                                            display: "Select Notification Type",
                                            value: Constants.emptyString,
                                        }}
                                        options={props.lookups?.notificationType.filter((x) =>
                                            !props.forceLinkToPage
                                                ? !questionnaireNotificationTypes.includes(x.key)
                                                : x
                                        )}
                                        disabled={
                                            props.forceLinkToPage ||
                                            props.notificationTypeId ===
                                            QuestionnaireNotificationType.survey
                                        }
                                    />
                                )}

                                <Form.Text name="title" label="Title" isLoading={props.loading} />
                                <Form.TextArea
                                    name="message"
                                    rows={3}
                                    label="Message"
                                    isLoading={props.loading}
                                />
                            </Form.PanelSection>

                            <Form.PanelSection
                                iconClassName="bi bi-link-45deg"
                                title="3. Link To"
                                style={{ marginTop: 16 }}
                            >
                                <Form.Dropdown
                                    name="referenceType"
                                    label="Link Type"
                                    isLoading={props.loading}
                                    disabled
                                    value={referenceType}
                                    blankOption={{
                                        key: Constants.emptyGuid,
                                        value: Constants.emptyString,
                                        display: "Select Reference Type",
                                    }}
                                    options={referenceTypeOptions}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        switch (e.target.value.toLowerCase()) {
                                            case "url":
                                                formikProps.setFieldValue("pageLinkId", "");
                                                setReferenceType("url");
                                                break;
                                            case "page":
                                                formikProps.setFieldValue("url", "");
                                                setReferenceType("page");
                                                break;
                                            default:
                                                formikProps.setFieldValue("pageLinkId", "");
                                                formikProps.setFieldValue("url", "");
                                                setReferenceType("url");
                                                break;
                                        }
                                    }}
                                    isHidden={true}
                                />
                                {referenceType === "url" && (
                                    <Form.Url
                                        name="externalUrl"
                                        label="Link Url"
                                        isLoading={props.loading}
                                    />
                                )}
                                <Form.Text
                                    name="actionLabel"
                                    label="Action Label"
                                    isLoading={props.loading}
                                />
                                {referenceType === "page" && (
                                    <Form.Dropdown
                                        key="pageLinkId"
                                        name="pageLinkId"
                                        label="Page Link"
                                        isLoading={props.loading}
                                        blankOption={{
                                            key: Constants.emptyGuid,
                                            value: Constants.emptyGuid,
                                            display: "Select Page Link",
                                        }}
                                        options={props.lookups?.pageLink}
                                        disabled={props.lockPageLink === true}
                                        isHidden={
                                            props.forceLinkToPage ||
                                            props.notificationTypeId ===
                                            QuestionnaireNotificationType.survey
                                        }
                                    />
                                )}
                            </Form.PanelSection>

                            <Form.PanelSection
                                iconClassName="bi bi-calendar-event"
                                title="4. Schedule"
                                style={{ marginTop: 16 }}
                            >
                                <Form.Datetime
                                    name="scheduledTimeUtc"
                                    type="datetime-local"
                                    label="Scheduled Date"
                                    placeholder="Enter date"
                                />
                                <Form.Switch
                                    name="quickReply"
                                    label="Quick Reply"
                                    isLoading={props.loading}
                                    checked={isQuickReply}
                                    onChange={(e: any) =>
                                        setIsQuickReply(e.target.checked ? true : undefined)
                                    }
                                />
                            </Form.PanelSection>
                        </>
                    );
                }}
            </Drawer.Form>
            {dialogProps && (
                <Dialog
                    contentText={dialogProps.contentText}
                    contentTitle={dialogProps.contentTitle}
                    onClose={() => setDialogProps(undefined)}
                    onConfirm={() => {
                        setDialogProps(undefined);
                        dialogProps?.onConfirm();
                    }}
                    open={dialogProps !== undefined}
                    closeText={dialogProps.closeText}
                    confirmText={dialogProps.confirmText}
                />
            )}
        </>
    );
};